import { ReactElement, useState } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Label } from "@twilio-paste/core/label";

import {
  ContactSupportProps,
  SupportCtaChannel,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";
import { useWiseOwlContext } from "../../../hooks";
import { copy, constants } from "../definitions";
import { MESSAGING_INSIGHTS_PRODUCT_CATEGORY } from "@twiliointernal/e2-commons-live-chat";
import { handleTicketSubmissionRedirect } from "../../utils";
/*
 * Displays CTAs to contact support and/or login. This will be primarily relevant
 * for customer-facing ingresses
 */
const ContactSupport = ({
  isLoggedIn,
  channel,
}: ContactSupportProps): ReactElement => {
  const [isAppLoading, setAppLoading] = useState<boolean>(false);
  const {
    chatId,
    ingress,
    handlers: {
      handleRedirectAction,
      handleGetChatSummary,
      handleLoginRedirect,
      handleToggleLiveChatPreEngagement,
      handleUpdateLiveChatPreEngagementValues,
    },
    applicationProps: { displayName, stargateUngatedUserId },
  } = useWiseOwlContext();

  const getParamsFromChatSummary = async () => {
    if (handleGetChatSummary) {
      setAppLoading(true);

      const chatSummary = await handleGetChatSummary();
      const { subject, description, wasTranslated } = chatSummary || {};
      const isChatSummaryEmpty = !subject && !description;

      return !isChatSummaryEmpty
        ? `&subject=${subject}&description=${description}&wasTranslated=${wasTranslated}`
        : "";
    }
  };

  const primaryCtaCopy = (): string => {
    if (!isLoggedIn) {
      return copy.contactSupport.ctas.login;
    }

    switch (channel) {
      case SupportCtaChannel.Ticket:
        return copy.contactSupport.ctas.gatedSubmitTicket;
      case SupportCtaChannel.LiveChat:
        return copy.contactSupport.ctas.liveChat;
      case SupportCtaChannel.HelpCenter:
      default:
        return copy.contactSupport.ctas.redirectToHelpCenter;
    }
  };

  /**
   * Console users: Redirect to OSC
   * OSC Authenticated users: Redirects to Submit a Ticket page
   * OSC Unauthenticated users: Redirects to Console login (same as navbar Login CTA)
   */
  const handlePrimaryCta = async () => {
    // Send users to help center
    if (channel === SupportCtaChannel.HelpCenter) {
      if (handleRedirectAction) {
        const redirectToHelpCenterUrl = chatId
          ? constants.redirectToHelpCenterChatUrl(chatId)
          : constants.redirectToHelpCenterUrl;

        handleRedirectAction(redirectToHelpCenterUrl);
      }

      return;
    }

    if (channel === SupportCtaChannel.LiveChat) {
      if (
        handleToggleLiveChatPreEngagement &&
        handleUpdateLiveChatPreEngagementValues &&
        handleGetChatSummary
      ) {
        setAppLoading(true);
        const chatSummary = await handleGetChatSummary();
        const { description = "" } = chatSummary || {};
        handleUpdateLiveChatPreEngagementValues({
          message: description,
          productCategory: MESSAGING_INSIGHTS_PRODUCT_CATEGORY,
        });
        setAppLoading(false);
        handleToggleLiveChatPreEngagement();
      }
    }

    if (channel === SupportCtaChannel.Ticket) {
      const additionalParams = await getParamsFromChatSummary();

      const url = isLoggedIn
        ? `${constants.submitTicketPage}?source=feedback&chatId=${chatId}${additionalParams}`
        : constants.loginPage;

      // Handle unauthenticated users
      const isOscUngated = ingress === WiseOwlIngress.oscUngated;
      if (isOscUngated && handleLoginRedirect) {
        handleLoginRedirect();
        return;
      }

      // Handle authenticated users
      if (handleRedirectAction) {
        handleRedirectAction(url);
        // Setting loading state to false after redirecting to the submit ticket page to avoid the button loading state being changed before the redirect
        setAppLoading(false);
      }

      return;
    }
  };

  const handleSecondaryCta = async () => {
    const additionalQueryParams = await getParamsFromChatSummary();

    if (chatId && handleRedirectAction) {
      handleTicketSubmissionRedirect({
        chatId,
        sessionId: stargateUngatedUserId,
        handleRedirectAction,
        additionalQueryParams,
      });

      // Setting loading state to false after redirecting to the submit ticket page to avoid the button loading state being changed before the redirect
      setAppLoading(false);
    }
  };

  return (
    <Box
      as="section"
      borderWidth="borderWidth10"
      borderColor="colorBorderWeaker"
      borderStyle="solid"
      borderRadius="borderRadius20"
      padding="space60"
      marginY="space40"
    >
      <Label htmlFor="message">{copy.contactSupport.label}</Label>
      <Box as="article" marginTop={"space30"}>
        <Box display="flex" columnGap={"space40"}>
          <Button
            onClick={handlePrimaryCta}
            variant="secondary"
            loading={isAppLoading}
          >
            {primaryCtaCopy()}
          </Button>

          {!isLoggedIn && (
            <Button onClick={handleSecondaryCta} variant="link">
              {copy.contactSupport.ctas.ungatedSubmitTicket}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContactSupport;
